import * as React from 'react'
import { Input } from '@/components/atoms/input'
import { cn } from '@/lib/utils'
import { Box } from '@/components/atoms/Box'
import { Text } from '@/components/atoms/Text'

/**
 * FormField molecule — combines a label, shadcn Input, and error message
 * into a single accessible form field.
 *
 * @atomic-tier Molecule
 * @category Forms
 */

export interface FormFieldProps {
  /** Unique field name used for id generation and the input `name` attribute */
  name: string
  /** Visible label text */
  label: string
  /** HTML input type */
  type?: React.HTMLInputTypeAttribute
  /** Placeholder text for the input */
  placeholder?: string
  /** Controlled value */
  value?: string
  /** Change handler */
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void
  /** Error message — when present, field renders in an error state */
  error?: string
  /** Whether the field is required (shows an asterisk after the label) */
  required?: boolean
  /** Whether the field is disabled */
  disabled?: boolean
  /** Additional class name for the wrapper div */
  className?: string
  /** Additional class name for the input element */
  inputClassName?: string
  /** HTML autocomplete attribute for the input */
  autoComplete?: string
}

export const FormField = React.forwardRef<HTMLInputElement, FormFieldProps>(
  (
    {
      name,
      label,
      type = 'text',
      placeholder,
      value,
      onChange,
      error,
      required = false,
      disabled = false,
      className,
      inputClassName,
      autoComplete,
    },
    ref,
  ) => {
    const fieldId = `field-${name}`
    const errorId = `${fieldId}-error`

    return (
      <Box className={cn('space-y-1.5', className)}>
        <label
          htmlFor={fieldId}
          className="block text-sm font-medium"
        >
          {label}
          {required && (
            <Text className="text-primary ml-0.5" aria-hidden="true">
              *
            </Text>
          )}
        </label>

        <Input
          ref={ref}
          id={fieldId}
          name={name}
          type={type}
          placeholder={placeholder}
          value={value}
          onChange={onChange}
          disabled={disabled}
          aria-label={label}
          aria-invalid={error ? true : undefined}
          aria-describedby={error ? errorId : undefined}
          aria-required={required}
          className={inputClassName}
          autoComplete={autoComplete}
        />

        {error && (
          <Text as="p"
            id={errorId}
            className="text-destructive text-sm"
            role="alert"
          >
            {error}
          </Text>
        )}
      </Box>
    )
  },
)

FormField.displayName = 'FormField'
